/**
 * Filtering shared by both tracker boards: which values a column offers, which
 * rows a selection keeps, and how many rows sit behind each value.
 *
 * Selections come in two shapes and compose with AND: the quick panel's
 * `BoardFilters` (column → chosen values) and the advanced builder's ordered
 * `FilterRule[]` (which adds `is not`). Both compile to the same
 * `CompiledFilter`, so one matcher serves both and a board can concatenate
 * them. Several values on one column are an OR.
 *
 * Date columns have no enumerable values, so they filter by BUCKET (overdue /
 * this month / later) computed against "now", never by literal date.
 */
import { format } from 'date-fns';
import {
  DATE_BUCKETS,
  FILTER_OPERATORS,
  compileConditions,
  filterKindForColumnType as kindForType,
  matchesFilter,
  conditionColumns,
  rowValueIds,
  storedValueIds,
  type CompiledFilter,
  type DateBucket,
  type FilterColumnKind,
  type FilterOperator,
} from '@inference.engine/tracker-filters/web';
import { v4 as uuidv4 } from 'uuid';

import type { BoardFilters } from './usePersistentFilters';
import type { CustomColumnType } from './useTrackersBoard';

export { DATE_BUCKETS, FILTER_OPERATORS };
export type { CompiledFilter, DateBucket, FilterColumnKind, FilterOperator };

export interface FilterValueOption {
  value: string;
  label: string;
  /** Leading-dot colour. Only status options carry one. */
  color?: string;
}

export interface FilterColumnSpec {
  id: string;
  label: string;
  kind: FilterColumnKind;
  options: ReadonlyArray<FilterValueOption>;
}

/**
 * Reads a row's raw value for a filter key. Defaults to the row's custom-column
 * values; the briefs board overrides it to reach its reserved Brief type field.
 */
export type RawValueReader<T> = (item: T, columnId: string) => unknown;

interface HasCustomValues {
  customColumnValues?: Record<string, unknown> | null;
}

export const readCustomColumnValue = <T extends HasCustomValues>(item: T, columnId: string) =>
  item.customColumnValues?.[columnId];

/** `null` for a type whose values are not worth enumerating (text, number, …). */
export const filterKindForColumnType = (type: CustomColumnType): FilterColumnKind | null =>
  kindForType(type);

/** The viewer's calendar day, which is what "today" means on the board. */
const viewerDay = (now: Date) => format(now, 'yyyy-MM-dd');

/**
 * All matching needs of a column. Deliberately less than `FilterColumnSpec`:
 * labels and options come from an async user directory, and a selection must
 * narrow the board on the first paint rather than once the names arrive.
 */
export type FilterMatchColumn = Pick<FilterColumnSpec, 'id' | 'kind'>;

/**
 * Match targets grown straight from the tracker's columns, skipping the ones
 * whose values are not worth enumerating. Pair it with `buildFilterColumns`,
 * which answers the different question of what the panel can DISPLAY.
 */
export function filterMatchColumns(
  columns: ReadonlyArray<Pick<FilterColumnSource, 'id' | 'type'>>,
): FilterMatchColumn[] {
  return conditionColumns(columns);
}

/**
 * Selections narrowed to columns that STILL exist. A persisted filter for a
 * since-deleted column would otherwise fail every row and silently empty the
 * board, with no chip left to clear it. An empty selection narrows nothing and
 * is dropped.
 */
export function compileFilters(
  filters: BoardFilters,
  columns: ReadonlyArray<FilterMatchColumn>,
): CompiledFilter[] {
  const kinds = new Map(columns.map((c) => [c.id, c.kind]));
  return Object.entries(filters).flatMap(([columnId, values]) => {
    const kind = kinds.get(columnId);
    if (!kind || values.length === 0) return [];
    return [{ columnId, kind, values: new Set(values) }];
  });
}

export interface FilterRule {
  /** Identity for reordering-free list keys; rules are otherwise duplicable. */
  id: string;
  columnId: string;
  operator: FilterOperator;
  values: readonly string[];
}

export function newFilterRule(columnId: string): FilterRule {
  return { id: uuidv4(), columnId, operator: 'is', values: [] };
}

/**
 * Rules narrowed the same way `compileFilters` narrows quick selections, with
 * one extra rule of its own: a rule with no values chosen yet is a half-written
 * condition, not a demand that every row match nothing, so it is dropped.
 */
export function compileRules(
  rules: readonly FilterRule[],
  columns: ReadonlyArray<FilterMatchColumn>,
): CompiledFilter[] {
  return compileConditions(rules, columns);
}

/**
 * Columns whose rule just started narrowing the board — one analytics event per
 * rule, fired when it first gets a value rather than when the empty row appears.
 */
export function newlyNarrowingColumnIds(
  before: readonly FilterRule[],
  after: readonly FilterRule[],
): string[] {
  const narrowedBefore = new Set(before.filter((r) => r.values.length > 0).map((r) => r.id));
  return after
    .filter((rule) => rule.values.length > 0 && !narrowedBefore.has(rule.id))
    .map((rule) => rule.columnId);
}

export function passesFilters<T>(
  item: T,
  compiled: readonly CompiledFilter[],
  read: RawValueReader<T>,
  now: Date,
): boolean {
  const today = viewerDay(now);
  return compiled.every((filter) => matchesFilter(read(item, filter.columnId), filter, today));
}

/**
 * The board's stand-in for the design's "matches name and notes": the item name
 * plus every free-text column value, Notes among them. `query` must already be
 * trimmed and lower-cased — callers do it once per pass, not once per row.
 */
export function passesSearch<T extends { title: string }>(
  item: T,
  query: string,
  textColumnIds: readonly string[],
  read: RawValueReader<T>,
): boolean {
  if (!query) return true;
  if (item.title.toLowerCase().includes(query)) return true;
  return textColumnIds.some((id) => {
    const raw = read(item, id);
    return typeof raw === 'string' && raw.toLowerCase().includes(query);
  });
}

/** Rows per value, keyed column id → value id → count. */
export type FilterValueCounts = ReadonlyMap<string, ReadonlyMap<string, number>>;

/**
 * Counts over the UNNARROWED row set: a chip answers "how many items have this
 * value", not "how many would survive if you added it to what is already
 * selected". Values with no rows still get a `0` so every chip shows a number.
 */
export function countFilterValues<T>(
  columns: ReadonlyArray<FilterColumnSpec>,
  items: readonly T[],
  read: RawValueReader<T>,
  now: Date,
): FilterValueCounts {
  const today = viewerDay(now);
  const counts = new Map<string, Map<string, number>>();
  for (const column of columns) {
    const perValue = new Map(column.options.map((o) => [o.value, 0]));
    for (const item of items) {
      for (const id of rowValueIds(read(item, column.id), column.kind, today)) {
        const current = perValue.get(id);
        if (current !== undefined) perValue.set(id, current + 1);
      }
    }
    counts.set(column.id, perValue);
  }
  return counts;
}

export interface FilterColumnSource {
  id: string;
  label: string;
  type: CustomColumnType;
  statusOptions?: ReadonlyArray<{ id: string; label: string; color: string }>;
}

export interface FilterOptionLabels {
  dateBuckets: Record<DateBucket, string>;
  /** Display name for a person id, or `undefined` while the directory is still
   *  loading and for an id it does not know. */
  personLabel: (id: string) => string | undefined;
}

/**
 * Panel columns grown from the tracker's own columns. Status offers every
 * configured option (an unused one still has to be pickable), people only the
 * ids actually assigned somewhere on the board — the workspace directory can
 * run to hundreds of names that would never match a row.
 *
 * A person we cannot name is left out rather than shown as a raw id, so a
 * people column appears only once the directory has loaded.
 */
export function buildFilterColumns<T>(
  columns: ReadonlyArray<FilterColumnSource>,
  items: readonly T[],
  read: RawValueReader<T>,
  labels: FilterOptionLabels,
  /** People to offer instead of those on the board, for a rule written before its rows exist. */
  people?: ReadonlyArray<FilterValueOption>,
): FilterColumnSpec[] {
  return columns.flatMap((column) => {
    const kind = filterKindForColumnType(column.type);
    if (!kind) return [];

    let options: FilterValueOption[];
    if (kind === 'status') {
      if (!column.statusOptions?.length) return [];
      options = column.statusOptions.map((o) => ({
        value: o.id,
        label: o.label,
        color: o.color,
      }));
    } else if (kind === 'date') {
      options = DATE_BUCKETS.map((bucket) => ({
        value: bucket,
        label: labels.dateBuckets[bucket],
      }));
    } else if (people) {
      options = [...people];
      if (options.length === 0) return [];
    } else {
      const present = new Set<string>();
      for (const item of items) {
        for (const id of storedValueIds(read(item, column.id))) present.add(id);
      }
      options = [...present]
        .flatMap((id) => {
          const label = labels.personLabel(id);
          return label ? [{ value: id, label }] : [];
        })
        .sort((a, b) => a.label.localeCompare(b.label));
      if (options.length === 0) return [];
    }

    return [{ id: column.id, label: column.label, kind, options }];
  });
}
